import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { usePageMeta } from "@/lib/use-page-meta";
import { StripePricingTable } from "@/components/StripePricingTable";
import { SOCIAL_LINKS } from "@/lib/socials";
import { PlainlyMark } from "@/components/PlainlyMark";
import { RelatedGuidesWidget } from "@/components/RelatedGuidesWidget";
import { Check, ArrowRight, LineChart, Sparkles, Wand2, FileText, Instagram, LogIn, ShieldCheck } from "lucide-react";

// Branded Plainly Google sign-in (uses our own Google OAuth client so the
// Google consent screen displays "Plainly", not "emergent.com").
const signInWithNext = (next = "/dashboard") => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  window.location.href = `${apiUrl}/api/auth/google/start?next=${encodeURIComponent(next)}`;
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageMeta({
    title: null, // use default (root page)
    description:
      "Plainly turns your Google Analytics into a plain-English weekly read-out. Connect GA directly or upload a CSV. AI-powered insights from Claude Sonnet 4.5. $18/month or $199/year for unlimited analyses + automatic weekly Monday emails. $79/month Agency plan for white-label client reports.",
    path: "/",
  });

  // Hardcoded so Google + AI Overviews both see the FAQ schema. Keep this
  // list in sync with the rendered FAQ section below.
  const FAQ_ITEMS = [
    {
      q: "Do I have to upload a CSV, or can I connect Google Analytics directly?",
      a: "Both work. You can connect your Google Analytics account with one click using read-only OAuth, or upload a GA4 CSV export. There's also a free demo dataset to try it before connecting your own data.",
    },
    {
      q: "Does Plainly send weekly reports automatically?",
      a: "Yes — the Business plan ($18/month or $199/year) includes an automatic Monday-morning email report sent every week from your default Google Analytics property.",
    },
    {
      q: "How much does Plainly cost?",
      a: "$18/month or $199/year for the Business plan (unlimited analyses + weekly email reports). $79/month for the Agency plan (Business + PDF export + white-label client reports). The yearly Business plan saves you 8% vs paying monthly.",
    },
    {
      q: "What AI does Plainly use?",
      a: "Plainly is powered by Anthropic's Claude Sonnet 4.5. Aggregated metrics for your selected periods are sent to Claude to generate the plain-English summary. Raw visitor-level data is never sent.",
    },
    {
      q: "Is my Google Analytics data safe?",
      a: "Yes. Plainly uses read-only Google Analytics OAuth (analytics.readonly scope) — we can never write to your account. Your data is not used to train AI models and is not sold or shared with advertisers. You can revoke access any time at myaccount.google.com/permissions.",
    },
    {
      q: "Can I cancel my monthly plan anytime?",
      a: "Yes. Cancel from the Billing page in your dashboard — your access stays active through the end of the paid period, then automatically downgrades. No questions asked.",
    },
  ];

  // Inject FAQPage JSON-LD on the homepage — this powers Google's FAQ rich
  // result and is the primary surface AI Overviews (Google) and ChatGPT/
  // Perplexity citations use to pull "what does Plainly do" answers from.
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "landing-faq-jsonld";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);

  const onCTA = () => {
    if (user) navigate("/dashboard");
    else signInWithNext("/dashboard");
  };

  // Pricing CTA — signed-in users see the live Stripe Pricing Table embedded
  // below the marketing cards (so this just scrolls them to it). Signed-out
  // users get bounced through Google sign-in and land back at /#pricing.
  const onBuyPlan = () => {
    if (!user) {
      signInWithNext("/#pricing");
      return;
    }
    document.getElementById("live-pricing-table")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 dotted-bg opacity-60" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <div className="lg:col-span-7 fade-up">
              <div className="tag-label text-[#E27D60] mb-6">AI Google Analytics translator</div>
              <div
                className="inline-flex items-center gap-2 rounded-full bg-white border border-[#1E392A]/15 px-3.5 py-1.5 text-xs text-[#1E392A] mb-5 bento-shadow"
                data-testid="hero-trust-badge"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-[#1E392A]" />
                <span><span className="font-semibold">Google-verified app</span> <span className="text-[#1E392A]/55">· Read-only access to your Analytics</span></span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                Your analytics,<br />
                <span className="italic text-[#E27D60]">finally</span> in plain English.
              </h1>
              <p className="mt-6 text-lg text-[#1E392A]/70 max-w-xl leading-relaxed">
                Plainly reads your Google Analytics and tells you what actually changed
                this week, why it happened, and the next thing you should try. No charts you have to decode.
                No jargon. Just a friendly read-out and a short to-do list.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  onClick={onCTA}
                  data-testid="hero-cta-btn"
                  className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white px-7 h-12 text-base"
                >
                  Get my first analysis <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <a
                  href="#pricing"
                  className="rounded-full h-12 px-6 inline-flex items-center text-sm font-medium border border-[#1E392A]/15 hover:bg-white"
                  data-testid="hero-pricing-link"
                >
                  See pricing
                </a>
              </div>
              <p className="mt-4 text-xs text-[#1E392A]/55" data-testid="hero-legal-notice">
                By signing in you agree to our{" "}
                <Link to="/terms" className="underline" data-testid="hero-terms-link">Terms</Link>
                {" "}and{" "}
                <Link to="/privacy" className="underline" data-testid="hero-privacy-link">Privacy Policy</Link>.
              </p>

              <div className="mt-10 flex items-center gap-6 text-sm text-[#1E392A]/60">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#1E392A]" /> 30-second setup</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#1E392A]" /> Upload CSV or connect Google</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#1E392A]" /> Claude Sonnet 4.5 powered</div>
              </div>
            </div>

            <div className="lg:col-span-5 fade-up-delay-2">
              <div className="relative rounded-3xl overflow-hidden bento-shadow border border-black/5 bg-white">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/ef06fbb4-7b49-42d0-9bf4-2c60c35a862e/images/4eb26f721bf2433cf611934639b22fa4843068ad724db58f51567b0ad22c8e23.png"
                  alt="Sample Plainly weekly Google Analytics report with plain-English insights" className="w-full h-56 object-cover" />
                <div className="p-6">
                  <div className="rounded-2xl bg-[#1E392A] text-[#F7F5F0] p-5">
                    <div className="flex items-center gap-2 text-[#85CDCB] mb-3">
                      <Sparkles className="w-3.5 h-3.5" />
                      <span className="tag-label">Plain English summary</span>
                    </div>
                    <ul className="space-y-2 text-[13.5px] leading-relaxed text-[#F7F5F0]/90">
                      {[
                        "Sessions up 18% — most of the lift came from organic search on your /pricing page.",
                        "Conversions up only 4%, meaning more traffic but not converting proportionally.",
                        "Instagram sent 540 sessions but only 0.4% converted — worth a closer look.",
                        "Email is your best channel at 9.2% — send a second campaign this week.",
                      ].map((s, i) => (
                        <li key={i} className="flex gap-2.5">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#E27D60] shrink-0" aria-hidden="true" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                    <div className="rounded-xl bg-[#F7F5F0] p-3">
                      <div className="tag-label text-[#1E392A]/50">Sessions</div>
                      <div className="font-display text-xl mt-1">+18%</div>
                    </div>
                    <div className="rounded-xl bg-[#F7F5F0] p-3">
                      <div className="tag-label text-[#1E392A]/50">Users</div>
                      <div className="font-display text-xl mt-1">+12%</div>
                    </div>
                    <div className="rounded-xl bg-[#F7F5F0] p-3">
                      <div className="tag-label text-[#1E392A]/50">Bounce</div>
                      <div className="font-display text-xl mt-1">-4%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-14 md:py-20" data-testid="who-this-is-for">
        <div className="text-center mb-8">
          <div className="tag-label text-[#E27D60]">Who this is for</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">If GA4 has ever overwhelmed you, you&apos;re in the right place.</h2>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2.5 max-w-3xl mx-auto">
          {[
            { t: "Small business owners", d: "no time to dig through dashboards but still need to know what's working." },
            { t: "Owners working with agencies", d: "independent visibility into what your \"web guy\" is actually doing." },
            { t: "Startups & beginners", d: "simple, plain-English insights without the learning curve." },
            { t: "Busy professionals", d: "you know analytics matter — Plainly does the reading for you." },
            { t: "Non-technical founders", d: "understand performance without a technical background." },
            { t: "Burned by confusing dashboards", d: "if GA4 feels overwhelming, Plainly turns chaos into clarity." },
            { t: "Teams making data-driven decisions", d: "the \"what's happening\" snapshot you need to act fast." },
            { t: "Freelancers & consultants", d: "show clients clear insights that make you look like a data hero." },
            { t: "Anyone who wants control back", d: "stop guessing — see the truth for yourself, in plain English." },
          ].map((p, i) => (
            <li key={i} className="flex gap-3 text-sm leading-snug py-1.5 border-b border-black/5 last:border-0 md:[&:nth-last-child(2)]:border-0" data-testid={`audience-card-${i}`}>
              <Check className="w-4 h-4 text-[#E27D60] shrink-0 mt-1" />
              <span><span className="font-semibold text-[#1E392A]">{p.t}</span> <span className="text-[#1E392A]/70">— {p.d}</span></span>
            </li>
          ))}
        </ul>

        <div className="mt-8 text-center">
          <Button
            onClick={onCTA}
            className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white h-11 px-6"
            data-testid="audience-cta-btn"
          >
            See what your data actually says <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 pb-16 md:pb-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="tag-label text-[#E27D60]">How it works</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 max-w-xl">Four easy steps. About a minute.</h2>
          </div>
          <p className="text-[#1E392A]/60 max-w-md">No dashboards to learn. No spreadsheets to crunch. We do the boring part and explain everything like a friendly coach would.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            { icon: <LogIn className="w-5 h-5" />, t: "Sign in with Google", d: "Tap \"Sign in with Google\" in the top-right corner. Try the free demo, or unlock the full version any time you're ready." },
            { icon: <FileText className="w-5 h-5" />, t: "Bring your data", d: "Connect your Google Analytics directly with one click — or upload a GA4 CSV export. (Demo dataset available too.)" },
            { icon: <Wand2 className="w-5 h-5" />, t: "Pick a period", d: "Use the calendar, or tap the day, week, or month preset." },
            { icon: <Sparkles className="w-5 h-5" />, t: "Get the read-out", d: "Plain-English summary, the why behind the numbers, and what to do next." },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-black/5 p-7 bento-shadow relative" data-testid={`how-step-${i + 1}`}>
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#1E392A] text-[#F7F5F0] grid place-items-center font-display text-sm shadow-md">{i + 1}</div>
              <div className="w-10 h-10 rounded-xl bg-[#F7F5F0] grid place-items-center text-[#1E392A] mb-5">{s.icon}</div>
              <div className="font-display text-xl lg:text-2xl">{s.t}</div>
              <p className="text-[#1E392A]/65 mt-2 text-sm lg:text-base">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/ef06fbb4-7b49-42d0-9bf4-2c60c35a862e/images/c25c46ee8cc3d927970152ab1c88e0aa8c2af8edd71fa66c2a9cc9b43717517e.png"
            alt="" className="w-full h-full object-cover opacity-60" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="tag-label text-[#E27D60]">Pricing</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3">Honest pricing. One unlimited plan, one for agencies.</h2>
          </div>

          {/* Business (2 cards) on the left gets 2x the column width so the
              flagship plan reads as the bigger, more important option. Agency
              sits in the right-hand third as the niche option. On mobile
              everything stacks vertically and the divider becomes a
              horizontal line. */}
          <div className="relative max-w-6xl mx-auto md:grid md:grid-cols-[3fr_auto_1fr] md:gap-8 flex flex-col gap-6 items-stretch">
            {/* Business group */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Business — Monthly */}
              <div className="rounded-3xl border-2 border-[#E27D60] bg-white p-7 bento-shadow relative flex flex-col">
                <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#E27D60] text-white text-xs font-medium tracking-wide">Most popular</div>
                <div className="tag-label text-[#1E392A]/60">Business · Monthly</div>
                <div className="font-display text-5xl mt-2">$18<span className="text-base font-sans text-[#1E392A]/60">/mo</span></div>
                <div className="text-sm text-[#1E392A]/60 mt-2">unlimited analyses, weekly emails</div>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#E27D60] mt-0.5" /> Unlimited AI analyses</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#E27D60] mt-0.5" /> Day / week / month + custom comparisons</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#E27D60] mt-0.5" /> Unlimited shareable links</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#E27D60] mt-0.5" /> Weekly reports straight to your email</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#E27D60] mt-0.5" /> Re-run any time</li>
                </ul>
                <Button onClick={onBuyPlan} data-testid="pricing-monthly-btn" className="mt-7 w-full rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11">
                  {user ? "Subscribe now" : "Sign in to subscribe"}
                </Button>
              </div>

              {/* Business — Yearly */}
              <div className="rounded-3xl border border-[#85CDCB]/60 bg-white p-7 bento-shadow relative flex flex-col">
                <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#85CDCB] text-[#1E392A] text-xs font-medium tracking-wide">Best value</div>
                <div className="tag-label text-[#1E392A]/60">Business · Yearly</div>
                <div className="font-display text-5xl mt-2 flex items-baseline gap-2">
                  <span>$199<span className="text-base font-sans text-[#1E392A]/60">/yr</span></span>
                </div>
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[#85CDCB]/25 text-[#1E392A] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider w-fit">
                  <Sparkles className="w-3 h-3" /> Save with yearly
                </div>
                <div className="text-sm text-[#1E392A]/60 mt-2">unlimited analyses, weekly emails — billed annually</div>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> Everything in Business Monthly</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> Pay for 11 months, get 12</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> Lock in today&apos;s price for a full year</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> One invoice, easy bookkeeping</li>
                </ul>
                <Button onClick={onBuyPlan} data-testid="pricing-yearly-btn" className="mt-7 w-full rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white h-11">
                  {user ? "Subscribe yearly" : "Sign in to subscribe"}
                </Button>
              </div>
            </div>

            {/* Vertical divider — becomes a horizontal line on mobile */}
            <div className="md:flex md:items-stretch md:px-0 my-2 md:my-0" aria-hidden="true">
              <div className="hidden md:block w-px bg-gradient-to-b from-transparent via-[#1E392A]/25 to-transparent" />
              <div className="md:hidden h-px bg-gradient-to-r from-transparent via-[#1E392A]/25 to-transparent w-full" />
            </div>

            {/* Agency */}
            <div className="rounded-3xl bg-[#1E392A] text-[#F7F5F0] p-6 bento-shadow relative flex flex-col">
              <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#85CDCB] text-[#1E392A] text-xs font-medium tracking-wide">Consultants & agencies</div>
              <div className="tag-label text-[#85CDCB]">Agency</div>
              <div className="font-display text-4xl mt-2">$79<span className="text-sm font-sans text-[#F7F5F0]/60">/mo</span></div>
              <div className="text-sm text-[#F7F5F0]/60 mt-2">white-label client reports</div>
              <ul className="mt-5 space-y-2 text-sm flex-1">
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> Everything in Business</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> Your logo, color, company on shared reports</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> PDF export</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5" /> Unlimited clients</li>
              </ul>
              <Button onClick={onBuyPlan} data-testid="pricing-agency-btn" className="mt-6 w-full rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11">
                {user ? "Subscribe now" : "Sign in to subscribe"}
              </Button>
            </div>
          </div>

          {/* Live Stripe Pricing Table — only render after sign-in so the
              post-payment redirect to /payment-success (which is auth-gated)
              can finalise the user's plan. Signed-out users see the marketing
              cards above and get bounced through Google sign-in first. */}
          {user && (
            <div id="live-pricing-table" className="mt-14 max-w-5xl mx-auto rounded-3xl bg-white/85 border border-black/10 p-6 md:p-8 bento-shadow backdrop-blur-sm">
              <div className="text-center max-w-xl mx-auto mb-6">
                <div className="tag-label text-[#E27D60]">Subscribe</div>
                <h3 className="font-display text-2xl sm:text-3xl mt-2">Pick a plan and pay securely with Stripe.</h3>
                <p className="text-sm text-[#1E392A]/60 mt-2">Cancel any time. You&apos;ll get access the moment payment confirms.</p>
              </div>
              <StripePricingTable email={user.email} userId={user.user_id} />
            </div>
          )}

          {/* Roadmap teaser */}
          <div className="mt-10 max-w-3xl mx-auto text-center" data-testid="roadmap-teaser">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-black/10 px-5 py-2 text-xs sm:text-sm text-[#1E392A]/75 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#E27D60]" />
              <span>
                More Plainly insights coming soon —{" "}
                <span className="font-medium text-[#1E392A]">including YouTube performance summaries.</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white border-t border-black/5">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="tag-label text-[#E27D60]">Frequently asked</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3">Common questions.</h2>
          </div>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-black/10 bg-[#F7F5F0] open:bg-white open:shadow-sm transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-5 md:p-6 select-none">
                  <span className="font-display text-lg md:text-xl text-[#1E392A]">{item.q}</span>
                  <span
                    aria-hidden="true"
                    className="w-7 h-7 rounded-full bg-[#1E392A]/5 text-[#1E392A] grid place-items-center text-lg leading-none transition-transform group-open:rotate-45 shrink-0"
                  >
                    +
                  </span>
                </summary>
                <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 text-[#1E392A]/75 leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
          <p className="text-center text-sm text-[#1E392A]/55 mt-8">
            Still have a question? <a href="mailto:office@myplainly.com" className="underline text-[#1E392A]">Email us</a>.
          </p>
        </div>
      </section>

      <footer className="border-t border-black/5 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          {/* Featured-on row — small trust badges centered above the legal/nav bar */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 pb-7 mb-7 border-b border-black/5" data-testid="footer-featured-on">
            <span className="tag-label text-[#1E392A]/45">Featured on</span>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://betalist.com/startups/plainly?utm_campaign=badge-plainly&utm_medium=badge&utm_source=badge-featured"
              data-testid="footer-betalist-badge"
              className="inline-block transition-opacity hover:opacity-80"
            >
              <img
                alt="Plainly - Plain-English Google Analytics for busy owners, AI driven and simple | BetaList"
                width="156"
                height="54"
                style={{ width: "156px", height: "54px" }}
                src="https://betalist.com/badges/featured?id=168343&theme=color"
              />
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[#1E392A]/60">
          <div className="flex items-center gap-2.5">
            <PlainlyMark className="w-6 h-6" />
            <span>Plainly · Analytics in plain English</span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/about" className="hover:text-[#1E392A]" data-testid="footer-about-link">About</Link>
            <Link to="/blog" className="hover:text-[#1E392A]" data-testid="footer-blog-link">Blog</Link>
            <Link to="/privacy" className="hover:text-[#1E392A]" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-[#1E392A]" data-testid="footer-terms-link">Terms</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("plainly:cookie-consent:open"))}
              className="hover:text-[#1E392A]"
              data-testid="footer-cookies-link"
            >
              Cookies
            </button>
            <a
              href={SOCIAL_LINKS.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Plainly on Instagram (${SOCIAL_LINKS.instagram.handle})`}
              className="hover:text-[#E27D60] transition-colors"
              data-testid="footer-instagram-link"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <span>© {new Date().getFullYear()} Plainly</span>
          </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
